package co.maytes.standupbot.dashboard.lib.auth

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.auth.TokenDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.post
import io.ktor.client.request.url
import io.ktor.http.isSuccess
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Access token lives in memory only (never `localStorage`). The refresh token
 * is an HttpOnly `SameSite=Strict` cookie owned by the backend — the browser
 * attaches it automatically to the `/auth/refresh` call below.
 *
 * `tryRefresh()` coalesces concurrent callers through [refreshMutex] + a shared
 * [Deferred]; if 10 requests hit 401 at once, only one refresh POST goes out.
 */
class TokenStore(
    private val http: HttpClient,
    private val baseUrl: String,
) {
    var accessToken: String? by mutableStateOf(null)
        private set

    private val refreshMutex = Mutex()
    private var inFlight: CompletableDeferred<Boolean>? = null

    suspend fun tryRefresh(): Boolean {
        // Fast path: another caller already has a refresh in progress — await it.
        inFlight?.let { return it.await() }

        return refreshMutex.withLock {
            inFlight?.let { return@withLock it.await() }
            val deferred = CompletableDeferred<Boolean>()
            inFlight = deferred
            try {
                val ok = CrossTabAuth.withRefreshLock { performRefreshWithRetry() }
                deferred.complete(ok)
                ok
            } catch (t: Throwable) {
                deferred.complete(false)
                false
            } finally {
                inFlight = null
            }
        }
    }

    private suspend fun performRefreshWithRetry(): Boolean {
        repeat(RETRY_DELAYS_MS.size) { attempt ->
            when (performRefresh()) {
                RefreshOutcome.Refreshed -> return true
                RefreshOutcome.Rejected -> return false
                RefreshOutcome.Transient -> delay(RETRY_DELAYS_MS[attempt])
            }
        }
        return performRefresh() == RefreshOutcome.Refreshed
    }

    private enum class RefreshOutcome { Refreshed, Rejected, Transient }

    private suspend fun performRefresh(): RefreshOutcome = try {
        val response = http.post {
            url("${baseUrl.trimEnd('/')}/auth/refresh")
        }
        when {
            response.status.isSuccess() -> {
                accessToken = response.body<TokenDto>().accessToken
                RefreshOutcome.Refreshed
            }
            response.status.value >= SERVER_ERROR_FLOOR || response.status.value == TOO_MANY_REQUESTS -> RefreshOutcome.Transient
            else -> {
                accessToken = null
                RefreshOutcome.Rejected
            }
        }
    } catch (t: CancellationException) {
        throw t
    } catch (t: Throwable) {
        RefreshOutcome.Transient
    }

    fun clear() {
        accessToken = null
    }

    private companion object {
        val RETRY_DELAYS_MS = longArrayOf(400, 1200)
        const val SERVER_ERROR_FLOOR = 500
        const val TOO_MANY_REQUESTS = 429
    }

    /** Convenience shape for other code paths that want a [Result] instead of a boolean. */
    suspend fun refreshOrUnauthorized(): Result<Unit, ApiError> =
        if (tryRefresh()) Result.Ok(Unit) else Result.Err(ApiError.Unauthorized)

    @Suppress("unused")
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    @Suppress("unused")
    private fun launchBackground(block: suspend CoroutineScope.() -> Unit) = scope.launch(block = block)
}
